import {
  Box,
  Checkbox,
  FormControlLabel,
  Paper,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import type { ChoiceOption } from './data';

interface SingleProps {
  multiple?: false;
  options: ChoiceOption[];
  value: string;
  onChange: (next: string) => void;
}

interface MultiProps {
  multiple: true;
  options: ChoiceOption[];
  value: string[];
  onChange: (next: string[]) => void;
}

type Props = SingleProps | MultiProps;

export default function ChoiceList(props: Props) {
  const { options } = props;

  const isSelected = (v: string) =>
    props.multiple ? props.value.includes(v) : props.value === v;

  const handleToggle = (v: string) => {
    if (props.multiple) {
      const next = props.value.includes(v)
        ? props.value.filter((x) => x !== v)
        : [...props.value, v];
      props.onChange(next);
    } else {
      props.onChange(v);
    }
  };

  return (
    <Stack spacing={1.5}>
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        return (
          <Paper
            key={opt.value}
            variant="outlined"
            onClick={() => handleToggle(opt.value)}
            sx={{
              p: 2,
              cursor: 'pointer',
              borderColor: selected ? 'primary.main' : 'divider',
              bgcolor: selected ? 'action.selected' : 'background.paper',
              transition: 'all 0.15s',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <FormControlLabel
              sx={{ width: '100%', m: 0, alignItems: 'flex-start', pointerEvents: 'none' }}
              control={
                props.multiple ? (
                  <Checkbox checked={selected} sx={{ mt: -0.5, ml: -1 }} />
                ) : (
                  <Radio checked={selected} sx={{ mt: -0.5, ml: -1 }} />
                )
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight={600}>
                    {opt.label}
                  </Typography>
                  {opt.description && (
                    <Typography variant="body2" color="text.secondary">
                      {opt.description}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Paper>
        );
      })}
    </Stack>
  );
}
